import React from "react";

import { ChevronDown, ChevronLeft, ChevronRight, Database } from "lucide-react";

import {
  formatActual,
  formatDMY,
  NO_SCORE_HEX,
  scoreHex,
  Tip,
  TrendGlyph,
  trendLine,
} from "./KpiDetailBlocks";
import type {
  XFactorKpiCategoryDetailModel,
  XFactorKpiMetricModel,
} from "../lib/deriveKpi";

type Mode = "normalized" | "actual";

const pct = (n: number | null | undefined) =>
  n != null && Number.isFinite(n) ? `${n.toFixed(1)}%` : "—";

/** The coloured dot + name + trend + value row both variants use. */
const DotRow: React.FC<{
  score: number | null;
  dashed: boolean;
  name: string;
  direction: string | null;
  value: string;
  tip: React.ReactNode;
}> = ({ score, dashed, name, direction, value, tip }) => (
  <Tip side="bottom" content={tip}>
    <div className="flex min-w-0 items-center gap-1.5 rounded-md px-1 py-0.5 hover:bg-gray-100/70">
      <span
        className={`inline-block h-4 w-4 shrink-0 rounded-full${
          dashed ? " border-2 border-dashed" : ""
        }`}
        style={{
          backgroundColor: dashed ? "transparent" : scoreHex(score),
          borderColor: dashed ? NO_SCORE_HEX : undefined,
        }}
      />
      <span className="min-w-0 flex-1 truncate text-[10.5px] leading-tight text-gray-700">
        {name}
      </span>
      <TrendGlyph direction={direction} />
      <span className="shrink-0 text-[10.5px] font-semibold tabular-nums">
        {value}
      </span>
    </div>
  </Tip>
);

/** Account-level Risk / Opportunity pill — Overall pane only. */
const RoBadge: React.FC<{ label: string; score: number | null }> = ({
  label,
  score,
}) => (
  <Tip
    side="bottom"
    content={`Mechanically computed ${label.toLowerCase()} score for the account. Higher is better on both axes.`}
  >
    <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-1.5 py-0.5">
      <span
        className={`inline-block h-3 w-3 shrink-0 rounded-full${
          score == null ? " border-2 border-dashed" : ""
        }`}
        style={{
          backgroundColor: score == null ? "transparent" : scoreHex(score),
          borderColor: score == null ? NO_SCORE_HEX : undefined,
        }}
      />
      <span className="text-[10.5px] text-gray-700">{label}</span>
      <span className="text-[10.5px] font-semibold tabular-nums">
        {score == null ? "—" : Math.round(score)}
      </span>
    </span>
  </Tip>
);

const ModeButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`h-6 rounded-full border px-2 text-[11px] font-medium transition-colors ${
      active
        ? "border-purple-500 bg-purple-500/10 text-purple-700"
        : "border-gray-200 text-gray-500 hover:text-gray-900"
    }`}
  >
    {children}
  </button>
);

const StepButton: React.FC<{
  onClick: () => void;
  disabled: boolean;
  label: string;
  children: React.ReactNode;
}> = ({ onClick, disabled, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 text-gray-800 hover:bg-gray-100 disabled:cursor-not-allowed disabled:border-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
  >
    {children}
  </button>
);

/** One metric at the stepper's date: that date's series point, else nothing. */
const MetricDot: React.FC<{
  metric: XFactorKpiMetricModel;
  mode: Mode;
  activeDate: string | null;
  measurementDate: string | null;
}> = ({ metric, mode, activeDate, measurementDate }) => {
  const atMeasure = !activeDate || activeDate === measurementDate;
  const point = activeDate
    ? (metric.series.find(p => p.date === activeDate) ?? null)
    : null;
  // On the measurement date, fall back to the row's own values when the
  // series is empty (a briefing rendered before series were returned).
  const normalized = point
    ? point.normalizedValue
    : atMeasure
      ? metric.normalizedValue
      : null;
  const actual = point ? point.value : atMeasure ? metric.value : null;
  const notWeighted =
    metric.nominalWeight != null && metric.nominalWeight <= 0.0001;
  const missing = normalized == null;

  const tip = (
    <div className="grid gap-0.5">
      {notWeighted ? (
        <div>Not weighted</div>
      ) : (
        <>
          <div>Assigned: {pct(metric.nominalWeight)}</div>
          <div>
            Applied:{" "}
            {atMeasure
              ? pct(metric.weight)
              : `shown only on measurement date (${formatDMY(
                  measurementDate
                )}). Historical values are evidence, not weighted inputs.`}
          </div>
        </>
      )}
      {trendLine(metric.trendDirection, metric.trendMagnitudePct)}
    </div>
  );

  return (
    <DotRow
      score={normalized}
      dashed={missing || notWeighted}
      name={metric.name}
      direction={metric.trendDirection}
      value={
        mode === "normalized"
          ? normalized == null
            ? "—"
            : String(Math.round(normalized))
          : formatActual(actual, metric.uom)
      }
      tip={tip}
    />
  );
};

/**
 * The collapsible card at the top of the modal body, xfactor's
 * MetricsDataAccordion. On Overall it is "Computed scores": one dot per
 * category with its trend, plus the account's computed Risk/Opportunity.
 * On a category it is "Metrics data": one dot per metric, a Normalized/
 * Actual toggle, and a date stepper across the history window.
 */
const KpiMetricsDataCard: React.FC<{
  detail: XFactorKpiCategoryDetailModel;
  /** Overall pane: every category, for the Computed Scores grid. */
  peers?: XFactorKpiCategoryDetailModel[];
  measurementDate: string | null;
}> = ({ detail, peers, measurementDate }) => {
  const [open, setOpen] = React.useState(true);
  const [mode, setMode] = React.useState<Mode>("normalized");
  const [dateIdx, setDateIdx] = React.useState(0);
  const isOverall = detail.categoryId === "overall";
  const metrics = detail.metrics;

  // Newest → oldest; the measurement date first when the account has data on it.
  const windowDates = React.useMemo(() => {
    const set = new Set<string>();
    if (measurementDate) set.add(measurementDate);
    detail.historyDates.forEach(d => set.add(d));
    metrics.forEach(m =>
      m.series.forEach(p => {
        if (p.date) set.add(p.date);
      })
    );
    return Array.from(set).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
  }, [measurementDate, detail.historyDates, metrics]);

  // A different pane starts back on the measurement date.
  React.useEffect(() => setDateIdx(0), [detail.categoryId]);

  const activeDate = windowDates[dateIdx] ?? measurementDate ?? null;

  const history = [...detail.historyDates].sort();
  const before = measurementDate
    ? history.filter(d => d < measurementDate)
    : history;
  const latestHistory = (before.length ? before : history)[
    (before.length ? before : history).length - 1
  ];
  const hasDepth = detail.historyDepthUsed != null;
  const datesLine =
    measurementDate || history.length || hasDepth ? (
      <span className="ml-auto truncate pl-3 text-[10.5px] font-normal normal-case text-gray-500">
        <strong className="font-semibold text-gray-900">Dates:</strong>{" "}
        {measurementDate ? <>measure - {formatDMY(measurementDate)}</> : null}
        {measurementDate && (history.length || hasDepth) ? "; " : null}
        {history.length || hasDepth ? (
          <>
            history -{" "}
            {history.length
              ? `${formatDMY(history[0])} to ${formatDMY(latestHistory)}`
              : "—"}
            {hasDepth ? ` (depth: ${detail.historyDepthUsed})` : null}
          </>
        ) : null}
      </span>
    ) : null;

  const weighted = metrics.filter(
    m => m.nominalWeight != null && m.nominalWeight > 0
  );
  const missingWeighted = weighted.filter(
    m => m.weight == null || m.weight <= 0.0001
  );
  const anyWeightDelta = metrics.some(
    m =>
      m.nominalWeight != null &&
      m.weight != null &&
      Math.abs(m.nominalWeight - m.weight) >= 1
  );

  return (
    <div className="rounded-md border border-gray-200 bg-white/60">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left"
      >
        <span className="flex shrink-0 items-center gap-1.5 text-sm font-semibold uppercase text-gray-900">
          <Database className="h-3.5 w-3.5 text-slate-500" aria-hidden />
          {isOverall
            ? `Computed scores (${peers?.length ?? 0})`
            : `Metrics data (${metrics.length})`}
        </span>
        {datesLine}
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>

      {open ? (
        <div className="px-3 pb-2">
          {isOverall ? (
            <>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 sm:grid-cols-3 lg:grid-cols-4">
                {(peers ?? []).map(c => (
                  <DotRow
                    key={c.categoryId}
                    score={c.score}
                    dashed={c.score == null}
                    name={c.categoryName}
                    direction={c.trendDirection}
                    value={c.score == null ? "—" : String(Math.round(c.score))}
                    tip={
                      <div className="grid gap-0.5">
                        <div>Assigned: {pct(c.assignedWeightPct)}</div>
                        <div>Applied: {pct(c.effectiveWeightPct)}</div>
                        {trendLine(c.trendDirection, c.trendMagnitudePct)}
                      </div>
                    }
                  />
                ))}
              </div>
              {detail.riskScore != null || detail.opportunityScore != null ? (
                <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-gray-200 pt-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    Computed R&amp;O
                  </span>
                  <RoBadge label="Risk" score={detail.riskScore} />
                  <RoBadge
                    label="Opportunity"
                    score={detail.opportunityScore}
                  />
                </div>
              ) : null}
            </>
          ) : !metrics.length ? (
            <p className="text-[11px] text-gray-500">
              No metrics payload persisted for this category. Regenerate the
              briefing to populate.
            </p>
          ) : (
            <>
              <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                <ModeButton
                  active={mode === "normalized"}
                  onClick={() => setMode("normalized")}
                >
                  Normalized
                </ModeButton>
                <ModeButton
                  active={mode === "actual"}
                  onClick={() => setMode("actual")}
                >
                  Actual
                </ModeButton>
                {windowDates.length ? (
                  <div className="ml-auto flex items-center gap-1 text-[11px] text-gray-500">
                    <StepButton
                      label="Older date"
                      disabled={dateIdx >= windowDates.length - 1}
                      onClick={() =>
                        setDateIdx(i => Math.min(windowDates.length - 1, i + 1))
                      }
                    >
                      <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
                    </StepButton>
                    <span className="min-w-[7.5rem] text-center tabular-nums">
                      <strong className="font-semibold text-gray-900">
                        Date:
                      </strong>{" "}
                      {formatDMY(activeDate)}
                      {activeDate === measurementDate ? (
                        <span className="ml-1 text-[10px] uppercase text-purple-600">
                          measure
                        </span>
                      ) : null}
                    </span>
                    <StepButton
                      label="Newer date"
                      disabled={dateIdx <= 0}
                      onClick={() => setDateIdx(i => Math.max(0, i - 1))}
                    >
                      <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                    </StepButton>
                  </div>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 sm:grid-cols-3 lg:grid-cols-4">
                {metrics.map(m => (
                  <MetricDot
                    key={m.metricId}
                    metric={m}
                    mode={mode}
                    activeDate={activeDate}
                    measurementDate={measurementDate}
                  />
                ))}
              </div>
              {anyWeightDelta || missingWeighted.length ? (
                <p className="mt-2 text-[10.5px] text-gray-500">
                  Weights rebalanced: {missingWeighted.length} of{" "}
                  {weighted.length} weighted metric
                  {weighted.length === 1 ? "" : "s"} missing data — Δ
                  redistributed pro rata.
                </p>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default KpiMetricsDataCard;
